import React from 'react';
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import ChartCard from './ChartCard';
import { money, monthLabel, pounds, whole } from './format';
import { AXIS_TEXT, BLUE, GRID, NAVY } from './palette';
import { svgLegend } from './svgLegend';

const MARKER = 4;

// Pounds are a solid line with round markers and dollars a dashed line with
// square ones, so the two series differ by shape and not only by color.
function circleDot({ cx, cy, index }) {
    return <circle key={index} cx={cx} cy={cy} r={MARKER} fill={NAVY} />;
}

function squareDot({ cx, cy, index }) {
    return (
        <rect
            key={index}
            x={cx - MARKER}
            y={cy - MARKER}
            width={MARKER * 2}
            height={MARKER * 2}
            fill={BLUE}
        />
    );
}

const LEGEND = svgLegend([
    {
        label: 'Pounds diverted',
        swatch: (
            <g>
                <line
                    x1="0"
                    x2="14"
                    y1="7"
                    y2="7"
                    stroke={NAVY}
                    strokeWidth="2"
                />
                <circle cx="7" cy="7" r="3.5" fill={NAVY} />
            </g>
        ),
    },
    {
        label: 'Dollars saved',
        swatch: (
            <g>
                <line
                    x1="0"
                    x2="14"
                    y1="7"
                    y2="7"
                    stroke={BLUE}
                    strokeWidth="2"
                    strokeDasharray="4 2"
                />
                <rect x="3.5" y="3.5" width="7" height="7" fill={BLUE} />
            </g>
        ),
    },
]);

function MonthlyTrendChart({ months }) {
    const data = months.map((row) => ({
        label: monthLabel(row.month),
        pounds: row.pounds,
        dollars: row.dollars,
    }));
    const first = data[0]?.label;
    const last = data[data.length - 1]?.label;

    return (
        <ChartCard
            title="Monthly trend"
            filename="furniture-exchange-monthly-trend.png"
            summary={
                'Pounds diverted (left axis) and dollars saved (right axis) ' +
                `for each month from ${first} to ${last}. Months with no ` +
                'transfers show zero.'
            }
            table={{
                corner: 'Month',
                columns: [
                    'Pounds diverted',
                    'Dollars saved',
                    'Transfers',
                    'Listings posted',
                ],
                rows: months.map((row) => ({
                    key: row.month,
                    label: monthLabel(row.month),
                    values: [
                        pounds(row.pounds),
                        money(row.dollars),
                        whole(row.transfers),
                        whole(row.listingsPosted),
                    ],
                })),
            }}
        >
            <ResponsiveContainer width="100%" height={300}>
                <LineChart
                    data={data}
                    margin={{ top: 34, right: 4, left: 0, bottom: 4 }}
                    accessibilityLayer={false}
                >
                    <CartesianGrid stroke={GRID} vertical={false} />
                    <XAxis
                        dataKey="label"
                        tick={AXIS_TEXT}
                        interval="preserveStartEnd"
                        minTickGap={12}
                    />
                    <YAxis
                        yAxisId="lb"
                        width={48}
                        tick={AXIS_TEXT}
                        tickFormatter={whole}
                    />
                    <YAxis
                        yAxisId="usd"
                        orientation="right"
                        width={56}
                        tick={AXIS_TEXT}
                        tickFormatter={money}
                    />
                    <Tooltip
                        formatter={(value, name) =>
                            name === 'dollars' ? money(value) : pounds(value)
                        }
                    />
                    <Line
                        yAxisId="lb"
                        dataKey="pounds"
                        name="pounds"
                        stroke={NAVY}
                        strokeWidth={2}
                        dot={circleDot}
                        activeDot={false}
                        isAnimationActive={false}
                    />
                    <Line
                        yAxisId="usd"
                        dataKey="dollars"
                        name="dollars"
                        stroke={BLUE}
                        strokeWidth={2}
                        strokeDasharray="6 4"
                        dot={squareDot}
                        activeDot={false}
                        isAnimationActive={false}
                    />
                    {LEGEND}
                </LineChart>
            </ResponsiveContainer>
        </ChartCard>
    );
}

export default MonthlyTrendChart;
